import { useLayoutEffect, useRef } from 'react'
import { Object3D, type InstancedMesh } from 'three'

export interface PropsPista3D {
  /** Comprimento ao longo de Z, metros. */
  comprimentoM?: number
  /** Faixas de rolamento (3,5 m cada). */
  faixas?: number
  /** Largura de cada acostamento. */
  acostamentoM?: number
}

const LARGURA_FAIXA = 3.5
const TRACO_M = 4
const INTERVALO_M = 8

const nenhumRaio = () => null

/**
 * Rodovia em linha reta ao longo de Z, centrada na origem: faixas de 3,5 m,
 * acostamentos, linha de bordo branca e eixo amarelo tracejado.
 *
 * Pista e acostamentos são adjacentes: nenhum plano de asfalto cobre outro.
 * O `polygonOffset` mantém o asfalto à frente do terreno/grade e a pintura
 * à frente do asfalto, inclusive no enquadramento distante.
 *
 * Os tracejados são uma malha instanciada: numa pista de 2 km são centenas
 * de traços, e uma malha por traço pesaria no celular. Nada aqui recebe
 * clique — a pista é chão, e o toque passa para o que estiver por cima.
 */
export function Pista3D({
  comprimentoM = 600,
  faixas = 2,
  acostamentoM = 2.5,
}: PropsPista3D) {
  const larguraRolamento = faixas * LARGURA_FAIXA
  const tracos = Math.floor(comprimentoM / INTERVALO_M)
  const instancias = useRef<InstancedMesh>(null)

  useLayoutEffect(() => {
    const malha = instancias.current
    if (!malha) return
    const auxiliar = new Object3D()
    for (let i = 0; i < tracos; i++) {
      auxiliar.position.set(
        0,
        0.012,
        -comprimentoM / 2 + INTERVALO_M * (i + 0.5),
      )
      auxiliar.rotation.set(-Math.PI / 2, 0, 0)
      auxiliar.updateMatrix()
      malha.setMatrixAt(i, auxiliar.matrix)
    }
    malha.instanceMatrix.needsUpdate = true
  }, [tracos, comprimentoM])

  return (
    <group>
      {acostamentoM > 0 &&
        [1, -1].map((lado) => (
          <mesh
            key={`acostamento-${lado}`}
            position={[
              (lado * (larguraRolamento + acostamentoM)) / 2,
              0.005,
              0,
            ]}
            rotation={[-Math.PI / 2, 0, 0]}
            receiveShadow
            raycast={nenhumRaio}
          >
            <planeGeometry args={[acostamentoM, comprimentoM]} />
            <meshStandardMaterial
              color="#2a2f36"
              roughness={0.95}
              polygonOffset
              polygonOffsetFactor={-2}
              polygonOffsetUnits={-2}
            />
          </mesh>
        ))}
      <mesh
        position={[0, 0.005, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
        raycast={nenhumRaio}
      >
        <planeGeometry args={[larguraRolamento, comprimentoM]} />
        <meshStandardMaterial
          color="#1f242a"
          roughness={0.92}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>
      {[1, -1].map((lado) => (
        <mesh
          key={lado}
          position={[(lado * larguraRolamento) / 2, 0.011, 0]}
          rotation={[-Math.PI / 2, 0, 0]}
          raycast={nenhumRaio}
        >
          <planeGeometry args={[0.15, comprimentoM]} />
          <meshStandardMaterial
            color="#e8edf2"
            roughness={0.6}
            polygonOffset
            polygonOffsetFactor={-3}
            polygonOffsetUnits={-3}
          />
        </mesh>
      ))}
      <instancedMesh
        ref={instancias}
        args={[undefined, undefined, tracos]}
        raycast={nenhumRaio}
      >
        <planeGeometry args={[0.15, TRACO_M]} />
        <meshStandardMaterial
          color="#f0b323"
          roughness={0.6}
          polygonOffset
          polygonOffsetFactor={-3}
          polygonOffsetUnits={-3}
        />
      </instancedMesh>
    </group>
  )
}
